import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatDuration, findSegmentIndex } from "../lib/playback";

function projectedElapsed(segments, currentTime, adaptive) {
  if (!adaptive || !segments?.length) return currentTime;
  return segments.reduce((total, segment) => {
    const played = Math.max(0, Math.min(currentTime, segment.end) - segment.start);
    return total + played / segment.speed;
  }, 0);
}

function projectedDuration(segments, duration, adaptive) {
  if (!adaptive || !segments?.length) return duration;
  return segments.reduce((total, segment) => total + (segment.end - segment.start) / segment.speed, 0);
}

export function PlaybackBar({
  segments = [],
  currentTime = 0,
  duration = 0,
  estimatedDuration,
  playbackRate = 1,
  availableRates = [1],
  playing = false,
  adaptive = true,
  original = false,
  seekTime,
  onSeekChange,
  onSeekCommit,
  onTogglePlayback,
  onModeChange,
  onManualRate,
  onToggleFullscreen,
  isImmersive = false,
  sponsorSkipRemaining,
  onSkipSponsor,
  planPending = false,
  onControlsActivity,
}) {
  const seekValueRef = useRef(currentTime);
  const seekingRef = useRef(false);
  const [seeking, setSeeking] = useState(false);
  seekValueRef.current = Number.isFinite(seekTime) ? seekTime : currentTime;
  const beginSeek = () => { seekingRef.current = true; setSeeking(true); };
  const finishSeek = useCallback(() => {
    if (!seekingRef.current) return;
    seekingRef.current = false;
    setSeeking(false);
    onSeekCommit(seekValueRef.current);
  }, [onSeekCommit]);
  const cancelSeek = useCallback(() => {
    seekingRef.current = false;
    setSeeking(false);
    onSeekChange?.(null);
  }, [onSeekChange]);
  useEffect(() => {
    const onVisibility = () => { if (document.visibilityState === "hidden") cancelSeek(); };
    window.addEventListener("pointerup", finishSeek);
    window.addEventListener("touchend", finishSeek);
    window.addEventListener("blur", cancelSeek);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pointerup", finishSeek);
      window.removeEventListener("touchend", finishSeek);
      window.removeEventListener("blur", cancelSeek);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [finishSeek, cancelSeek]);
  const previewTime = seeking && Number.isFinite(seekTime) ? seekTime : null;
  const activeIndex = findSegmentIndex(segments, currentTime);
  const manualRate = !adaptive && !original && playbackRate !== 1 ? playbackRate : null;
  const total = useMemo(() => {
    if (manualRate) return duration / manualRate;
    if (adaptive && Number.isFinite(estimatedDuration)) return estimatedDuration;
    return projectedDuration(segments, duration, adaptive);
  }, [segments, duration, adaptive, estimatedDuration, manualRate]);
  const projectTime = (time) => manualRate ? time / manualRate : projectedElapsed(segments, time, adaptive);
  const elapsed = projectTime(currentTime);
  const displayTime = previewTime !== null ? projectTime(previewTime) : elapsed;
  const estimatePending = adaptive && planPending;
  const showDurationComparison = ((adaptive && segments.length > 0) || manualRate) && !estimatePending;
  const skipBy = (seconds) => {
    onSeekCommit(Math.max(0, Math.min(duration, currentTime + seconds)));
    onControlsActivity?.();
  };

  return (
    <section className="playback-bar" aria-label="Video playback controls" onPointerMove={onControlsActivity} onFocusCapture={onControlsActivity}>
      <div className="playback-bar__top">
        <div className="playback-bar__left">
          <div className="playback-bar__time" aria-live="off"><strong>{formatDuration(displayTime)}</strong><span>/</span>{estimatePending ? <span className="playback-bar__duration-pending" role="status" aria-label="Calculating adaptive duration" title="Calculating adaptive duration"><svg className="duration-spinner" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="7" /></svg></span> : <>{showDurationComparison && <s className="playback-bar__original-duration" title="Original duration">{formatDuration(duration)}</s>}<span className={showDurationComparison ? "playback-bar__theofast-duration" : undefined} title={showDurationComparison ? "TheoFast duration" : undefined}>{formatDuration(total)}</span></>}</div>
        </div>
        <div className="playback-bar__right">
          <label className="playback-bar__rate-picker" title="Choose playback speed">
            <span className="sr-only">Playback speed</span>
            <select aria-label="Playback speed" value={playbackRate} onChange={(event) => onManualRate?.(Number(event.target.value))}>
              {[...new Set([...(availableRates || []), playbackRate])].sort((a, b) => a - b).map((rate) => <option key={rate} value={rate}>{Number(rate).toFixed(2)}×</option>)}
            </select>
          </label>
          <div className="playback-mode-toggle playback-mode-toggle--bar" role="group" aria-label="Playback mode">
            <button type="button" className={original ? "is-selected" : ""} aria-pressed={original} onClick={() => onModeChange(false)}>Original</button>
            <button type="button" className={adaptive ? "is-selected" : ""} aria-pressed={adaptive} onClick={() => onModeChange(true)}>TheoFast</button>
          </div>
        </div>
      </div>

      <div className="playback-bar__transport-row">
        <div className="playback-bar__transport" role="group" aria-label="Playback and 15-second seeking">
          <button className="playback-bar__jump" type="button" onClick={() => skipBy(-15)} disabled={!duration} aria-label="Rewind 15 seconds" title="Rewind 15 seconds">
            <svg viewBox="0 0 512 512" aria-hidden="true"><g transform="translate(512 0) scale(-1 1)"><path d="M320 146s24.36-12-64-12A160 160 0 1 0 416 294" /><polyline points="256 58 336 138 256 218" /></g></svg><span aria-hidden="true">15</span>
          </button>
          <button className="playback-bar__play" type="button" onClick={onTogglePlayback} aria-label={playing ? "Pause video" : "Play video"}>
            {playing ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14M16 5v14" /></svg> : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8 5 11 7-11 7z" /></svg>}
          </button>
          <button className="playback-bar__jump" type="button" onClick={() => skipBy(15)} disabled={!duration} aria-label="Forward 15 seconds" title="Forward 15 seconds">
            <svg viewBox="0 0 512 512" aria-hidden="true"><path d="M320 146s24.36-12-64-12A160 160 0 1 0 416 294" /><polyline points="256 58 336 138 256 218" /></svg><span aria-hidden="true">15</span>
          </button>
        </div>
        <div className="playback-bar__skiprow">
        {Number.isFinite(sponsorSkipRemaining) && <button className="playback-bar__skip" type="button" onClick={onSkipSponsor}>Skip sponsor <span>{formatDuration(sponsorSkipRemaining)}</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5v14l11-7zM19 5v14" /></svg></button>}
        {!isImmersive && <button className="playback-bar__fullscreen" type="button" onClick={onToggleFullscreen} aria-label="Open immersive player" title="Open immersive player">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 4H4v4M16 4h4v4M20 16v4h-4M4 16v4h4" /></svg>
        </button>}
        </div>
      </div>

      <div className="playback-sections-viewport" aria-label="Playback speed map; click a section to seek">
        <div className="playback-sections" style={{ "--section-count": segments.length }}>
          {segments.map((segment, index) => {
            const speed = adaptive ? segment.speed : (manualRate || 1);
            const progress = currentTime <= segment.start ? 0 : currentTime >= segment.end ? 100 : ((currentTime - segment.start) / (segment.end - segment.start)) * 100;
            const active = index === activeIndex;
            return (
              <button
                type="button"
                key={`${segment.start}-${segment.end}`}
                className={`playback-section playback-section--${adaptive && segment.category === "fast" ? "fast" : "normal"} ${active ? "is-active" : ""}`}
                style={{ flexBasis: `${((segment.end - segment.start) / (duration || 1)) * 100}%` }}
                title={`${formatDuration(segment.start)}–${formatDuration(segment.end)} · ${speed.toFixed(2)}×${segment.reason ? ` · ${segment.reason}` : ""}`}
                aria-label={`Seek to ${formatDuration(segment.start)}, ${speed.toFixed(2)} times speed${segment.reason ? `, ${segment.reason}` : ""}`}
                onClick={() => onSeekCommit(segment.start)}
              >
                <span className="playback-section__track"><i style={{ width: `${progress}%` }} /></span>
              </button>
            );
          })}
        </div>
      </div>

      <input
        className="playback-bar__scrubber"
        type="range"
        min="0"
        max={duration || 1}
        step="0.1"
        value={previewTime !== null ? previewTime : currentTime}
        style={{ "--played-progress": `${Math.max(0, Math.min(100, ((previewTime !== null ? previewTime : currentTime) / (duration || 1)) * 100))}%` }}
        aria-label="Seek through video"
        onPointerDown={event => { beginSeek(); event.currentTarget.setPointerCapture?.(event.pointerId); }}
        onTouchStart={beginSeek}
        onKeyDown={event => { if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "PageUp", "PageDown"].includes(event.key)) beginSeek(); }}
        onChange={event => {
          seekValueRef.current = Number(event.target.value);
          // Safari can send its final range change after touchend, or without
          // a pointer gesture. Commit it directly rather than pinning a preview.
          if (seekingRef.current) onSeekChange(seekValueRef.current);
          else onSeekCommit(seekValueRef.current);
        }}
        onPointerUp={finishSeek}
        onTouchEnd={finishSeek}
        onPointerCancel={cancelSeek}
        onTouchCancel={cancelSeek}
        onLostPointerCapture={finishSeek}
        onBlur={finishSeek}
        onKeyUp={finishSeek}
      />
    </section>
  );
}
